import { config } from "@/config";
import { ArmyRecordData } from "@/schema/latest";
import {
  createUseInvokeClient,
  Failure,
  FailurePayload,
  IndieTabletopClient,
  Success,
} from "@indietabletop/appkit";
import { ownedProduct } from "@indietabletop/types";
import { array, boolean, number, object, optional, record, string, unknown } from "superstruct";

export function failureToString(failure: FailurePayload): string {
  switch (failure.type) {
    case "API_ERROR": {
      return `API responded with error status code: ${failure.code}`;
    }

    case "NETWORK_ERROR": {
      return `Could not reach the server due to a network error.`;
    }

    case "VALIDATION_ERROR": {
      return `Incoming data is using an outdated format. Try refreshing the app.`;
    }

    case "UNKNOWN_ERROR":
    default: {
      return `Unknown error.`;
    }
  }
}

class HobgoblinClient extends IndieTabletopClient {
  override async pullUserData(props: Parameters<IndieTabletopClient["pullUserData"]>[0]) {
    // Since we are overriding this method to be sepecifically Hobgoblin focused, we don't need
    // to use the props.includes value. expectCurrentUserId is not supported by
    // the legacy entrypoint.

    const data = await this.hobgoblinPullChanges({ sinceTs: props.sinceTs });
    return data.mapSuccess((value) => ({ hobgoblin: { armies: value } }));
  }

  override async pushUserData(props: Parameters<IndieTabletopClient["pushUserData"]>[0]) {
    const data = await this.hobgoblinPushAndPullChanges({
      pushTs: props.currentSyncTs,
      pullSinceTs: props.pullSinceTs,
      armies: props.data.hobgoblin.armies,
    });

    return data.mapSuccess((value) => ({ hobgoblin: { armies: value.armies } }));
  }

  /**
   * Pulls changes data from the cloud.
   *
   * Setting the `sinceTs` param to null signals that this is the first pull on
   * this client and therefore deletes can be omitted.
   */
  async hobgoblinPullChanges(props: { sinceTs: number | null }) {
    const params = new URLSearchParams({
      sinceTs: `${props.sinceTs ?? 0}`,
      omitDeleted: `${!props.sinceTs}`,
    });
    return (await this.fetchWithAuth(`/hobgoblin/data?${params}`, array())) as
      | Success<ArmyRecordData[]>
      | Failure<FailurePayload>;
  }

  async hobgoblinPushAndPullChanges(props: {
    pushTs: number;
    pullSinceTs: number | null;
    armies: ArmyRecordData[];
  }) {
    if (props.armies.length === 0) {
      return new Success({ synced_count: 0, armies: [] });
    }

    return (await this.fetchWithAuth(
      `/hobgoblin/data`,
      object({ synced_count: number(), armies: array() }),
      {
        method: "PATCH",
        json: {
          armies: props.armies,
          syncedTs: props.pushTs,
          pullSinceTs: props.pullSinceTs ?? 0,

          // If pullSinceTs is null we are performing the first ever sync
          // on this client so it is safe to omit syncing deletes.
          pullOmitDeleted: !props.pullSinceTs,
        },
      },
    )) as Success<{ synced_count: number; armies: ArmyRecordData[] }> | Failure<FailurePayload>;
  }

  async postImageToStore(
    file: File,
    presigned: { fields: Record<string, string>; url: string; key: string },
  ): Promise<Success<string> | Failure<FailurePayload>> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(presigned.fields)) {
      formData.append(key, value);
    }
    formData.append("file", file);

    try {
      const upload = await fetch(presigned.url, { method: "POST", body: formData });

      if (!upload.ok) {
        return new Failure({ type: "API_ERROR", code: upload.status });
      }

      return new Success(`/${presigned.key}`);
    } catch {
      return new Failure({ type: "NETWORK_ERROR" });
    }
  }

  async getPresignedUploadUrl(file: File) {
    return await this.fetchWithAuth(
      `/v1/images`,
      object({
        key: string(),
        fields: record(string(), string()),
        url: string(),
      }),
      {
        method: "POST",
        json: { size: file.size, type: file.type, name: file.name },
      },
    );
  }

  async getCheckoutSessionV2(sessionId: string) {
    return await this.fetch(
      `/v1/stripe/sessions/${sessionId}`,
      object({
        status: string(),
        orderRef: string(),
        products: array(ownedProduct()),
        dtrpg: optional(object({ allProductsDelivered: boolean() })),
      }),
    );
  }

  async shareToDtrpg(sessionId: string, email: string) {
    return await this.fetch(
      `/v1/stripe/sessions/${sessionId}/dtrpg`,
      object({ message: string() }),
      { method: "POST", json: { email } },
    );
  }

  async uploadImage(file: File) {
    const presignedOp = await this.getPresignedUploadUrl(file);

    if (!presignedOp.isSuccess) {
      return presignedOp;
    }

    return await this.postImageToStore(file, presignedOp.value);
  }
}

export const client = new HobgoblinClient({
  apiOrigin: config.ITC_API_ORIGIN,
  validation: {
    snapshot: unknown(),
    gameData: unknown(),
    ruleset: unknown(),
  },
});

export const useInvokeClient = createUseInvokeClient(client);
